import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { sizeLabel } from "@/lib/sizes";

const STATUS_STYLE = {
  upcoming: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const MatchedParcels = ({ items }) => {
  if (!items.length) return <span className="text-slate-400 italic text-xs">none</span>;
  return (
    <div className="space-y-1">
      {items.map((m) => (
        <Link key={m.id} to={`/admin/parcels/${m.parcel_id}`} data-testid={`trip-parcel-link-${m.parcel_id}`}
              className="block text-xs hover:underline">
          <span className="font-mono">{m.parcel_id.slice(0, 8)}</span>
          <span className="text-slate-500"> · {m.parcel?.pickup_location} → {m.parcel?.drop_location} · {sizeLabel(m.parcel)} · {m.status.replace("_", " ")}</span>
        </Link>
      ))}
    </div>
  );
};

const TripRow = ({ t }) => (
  <tr data-testid={`trip-row-${t.id}`} className="hover:bg-slate-50 align-top">
    <td className="px-4 py-3">
      <div className="font-mono text-xs">{t.id.slice(0, 8)}</div>
      <div className="text-[10px] uppercase text-slate-400">{t.travel_mode}</div>
    </td>
    <td className="px-4 py-3 text-xs">
      {t.carrier ? <Link to={`/admin/users/${t.carrier.id}`} className="hover:underline">{t.carrier.name || "—"}</Link> : "—"}
      <div className="text-slate-400">{t.carrier?.phone}</div>
    </td>
    <td className="px-4 py-3 text-xs">{t.origin} → {t.destination}
      {t.distance_km ? <div className="text-slate-400">{Number(t.distance_km).toFixed(0)} km</div> : null}
    </td>
    <td className="px-4 py-3 text-xs tabular-nums">{t.date}{t.departure_time ? <div className="text-slate-400">{t.departure_time}</div> : null}</td>
    <td className="px-4 py-3"><Badge className={`text-[10px] ${STATUS_STYLE[t.status]}`}>{t.status.toUpperCase()}</Badge></td>
    <td className="px-4 py-3"><MatchedParcels items={t.matched_parcels || []}/></td>
  </tr>
);

const Trips = () => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dateFrom, setFrom] = useState("");
  const [dateTo, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 50 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = { q, status, date_from: dateFrom, date_to: dateTo, page, page_size: 50 };
    Object.keys(params).forEach((k) => (params[k] === "" || params[k] === "all") && delete params[k]);
    let stale = false;
    const timer = setTimeout(() => {
      setLoading(true);
      adminApi.get("/trips", { params })
        .then((r) => { if (!stale) setData(r.data); })
        .catch((e) => { if (!stale) toast.error(e?.response?.data?.detail || "Couldn't load trips"); })
        .finally(() => { if (!stale) setLoading(false); });
    }, 250);
    return () => { stale = true; clearTimeout(timer); };
  }, [q, status, dateFrom, dateTo, page]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 50))), [data]);
  const reset = (fn) => (v) => { fn(v); setPage(1); };

  return (
    <div className="p-8">
      <h1 className="font-display text-2xl font-bold text-slate-900">Trips</h1>
      <p className="text-sm text-slate-500 mt-1 mb-6">Every trip posted by carriers, with the parcels matched to it.</p>

      <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Search</label>
          <div className="relative mt-1">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400"/>
            <Input data-testid="trips-search" value={q} onChange={(e) => reset(setQ)(e.target.value)} placeholder="Carrier name, phone, city or trip ID" className="pl-8"/>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Status</label>
          <Select value={status} onValueChange={reset(setStatus)}>
            <SelectTrigger data-testid="trips-filter-status" className="mt-1 w-[150px]"><SelectValue/></SelectTrigger>
            <SelectContent>
              {["all", "upcoming", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All" : s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Travel date from</label>
          <Input data-testid="trips-filter-from" type="date" value={dateFrom} onChange={(e) => reset(setFrom)(e.target.value)} className="mt-1"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Travel date to</label>
          <Input data-testid="trips-filter-to" type="date" value={dateTo} onChange={(e) => reset(setTo)(e.target.value)} className="mt-1"/>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100 flex items-center justify-between">
          <span data-testid="trips-total">{data.total} trips</span>
          <span>Page {page} of {totalPages}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Trip</th>
                <th className="px-4 py-2 text-left">Carrier</th>
                <th className="px-4 py-2 text-left">Route</th>
                <th className="px-4 py-2 text-left">Travel date</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Matched parcels</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((t) => <TripRow key={t.id} t={t}/>)}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-400" data-testid="trips-empty">No trips match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="trips-pg-prev">← Prev</Button>
        <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages} data-testid="trips-pg-next">Next →</Button>
      </div>
    </div>
  );
};

export default Trips;
